import { useTooltip } from '@/hooks/useTooltip';
import { formatNumber, monthFullLabel, monthLabel } from '@/lib/dashboardFormat';

export interface GroupedBarsPoint {
  /** YYYY-MM. */
  month: string;
  first: number;
  second: number;
}

/** Столбик с типовым тултипом страницы — отдельный компонент ради своего useTooltip. */
function Bar({ tone, heightPercent, label }: { tone: 'first' | 'second'; heightPercent: number; label: string }) {
  const tooltip = useTooltip<HTMLDivElement>(label);

  return (
    <div
      ref={tooltip.ref}
      className={`grouped-bars__bar grouped-bars__bar--${tone}`}
      style={{ height: `${heightPercent}%` }}
      {...tooltip.handlers}
    >
      {tooltip.node}
    </div>
  );
}

/**
 * Две серии столбиков по месяцам — выдано и списано у.е. Столбики стоят рядом,
 * а не друг на друге: сравнивают именно их между собой, а не сумму.
 *
 * Высота в процентах от общего максимума обеих серий — иначе одинаковые по
 * высоте столбики означали бы разные величины.
 */
export function GroupedBars({
  points,
  firstLabel,
  secondLabel,
}: {
  points: GroupedBarsPoint[];
  firstLabel: string;
  secondLabel: string;
}) {
  const max = Math.max(1, ...points.flatMap((p) => [p.first, p.second]));
  const empty = points.every((p) => p.first === 0 && p.second === 0);

  if (empty) {
    return <p className="chart__empty">За этот период операций не было.</p>;
  }

  return (
    <div className="grouped-bars">
      <div className="grouped-bars__plot">
        {points.map((point) => (
          <div className="grouped-bars__group" key={point.month}>
            <div className="grouped-bars__pair">
              <Bar
                tone="first"
                heightPercent={(point.first / max) * 100}
                label={`${monthFullLabel(point.month)} · ${firstLabel}: ${formatNumber(point.first)}`}
              />
              <Bar
                tone="second"
                heightPercent={(point.second / max) * 100}
                label={`${monthFullLabel(point.month)} · ${secondLabel}: ${formatNumber(point.second)}`}
              />
            </div>
            <span className="grouped-bars__label">{monthLabel(point.month)}</span>
          </div>
        ))}
      </div>

      <ul className="chart-legend">
        <li className="chart-legend__item chart-legend__item--first">{firstLabel}</li>
        <li className="chart-legend__item chart-legend__item--second">{secondLabel}</li>
      </ul>
    </div>
  );
}
